import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import Modal from 'components/Modal';
import DatePickerComponent from 'components/Datepicker/DatePickerComponent';
import H5 from 'components/H5';
import { Colors } from 'modules/theme';
import Icon from 'components/Icon';
import ButtonText from 'components/ButtonText';
import PropTypes from 'prop-types';

const INPUT_RADIUS = '8px';

const Shell = styled.div`
  max-width: 560px;
  margin: 0 auto;
`;

const Section = styled.section`
  background: #f8fbff;
  border: 1px solid #e8f1f8;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 14px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
`;

const SectionTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${Colors.blue};
`;

const PresetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
`;

const PresetButton = styled.button`
  border: 1px solid ${props => (props.active ? Colors.lightBlue : '#d7e6f2')};
  background: ${props => (props.active ? '#e8f4fd' : '#fff')};
  color: ${props => (props.active ? Colors.lightBlue : '#4a5568')};
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${Colors.lightBlue};
    color: ${Colors.lightBlue};
    background: #eef7fd;
  }
`;

const DateGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;

  @media (min-width: 520px) {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${Colors.lightBlue};
  margin-bottom: 6px;
  letter-spacing: 0.01em;
`;

const DateInputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .react-datepicker-wrapper {
    flex: 1;
    min-width: 0;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const DataTypeChip = styled.button`
  flex: 1 1 140px;
  border: 1px solid ${props => (props.active ? Colors.lightBlue : '#d7e6f2')};
  background: ${props => (props.active ? Colors.lightBlue : '#fff')};
  color: ${props => (props.active ? '#fff' : '#4a5568')};
  border-radius: ${INPUT_RADIUS};
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${props => (props.active ? '0 2px 8px rgba(20, 152, 203, 0.25)' : 'none')};

  &:hover {
    border-color: ${Colors.lightBlue};
    color: ${props => (props.active ? '#fff' : Colors.lightBlue)};
  }
`;

const EmailInput = styled.input`
  width: 100%;
  height: 44px;
  border: 1px solid ${props => (props.hasError ? Colors.red : '#d7e6f2')};
  box-sizing: border-box;
  border-radius: ${INPUT_RADIUS};
  font-size: 14px;
  color: ${Colors.gray};
  padding: 0 14px;
  outline: none;
  background: #fff;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: ${props => (props.hasError ? Colors.red : Colors.lightBlue)};
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const EmailHint = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: #64748b;
`;

const EmailError = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: ${Colors.red};
`;

const SummaryBar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fff;
  border: 1px solid #e8f1f8;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
`;

const SummaryText = styled(H5)`
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.45;
`;

const Footer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 4px;

  button {
    border-radius: ${INPUT_RADIUS} !important;
    min-width: 120px;
  }
`;

const CSV_DATA_TYPES = [
  { value: 1, label: 'Raw Data' },
  { value: 2, label: 'Calculated Data' },
  { value: 3, label: 'All data' },
];

const QUICK_RANGES = [
  { id: '24h', label: 'Last 24 hours', hours: 24 },
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: 'install', label: 'Since installation', useMinDate: true },
];

const isValidEmail = value => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const getActivePreset = (dateFrom, dateTo, minDate) => {
  const from = moment(dateFrom);
  const to = moment(dateTo);
  const now = moment();

  if (!from.isValid() || !to.isValid()) return null;
  if (!to.isSame(now, 'minute') && !to.isAfter(now.clone().subtract(2, 'minutes'))) return null;

  const hoursDiff = to.diff(from, 'hours', true);
  const daysDiff = to.diff(from, 'days', true);

  if (Math.abs(hoursDiff - 24) < 0.5) return '24h';
  if (Math.abs(daysDiff - 7) < 0.5) return '7d';
  if (Math.abs(daysDiff - 30) < 1) return '30d';
  if (minDate && from.isSame(moment(minDate), 'minute')) return 'install';

  return null;
};

const getDataTypeLabel = csvType =>
  CSV_DATA_TYPES.find(type => type.value === csvType)?.label ?? 'Raw Data';

const ModalCsvFiles = ({
  isVisible,
  switchModal,
  dateFrom,
  dateTo,
  handleDateFrom,
  handleDateTo,
  changeCsvType,
  csvType,
  saveChanges,
  minDate,
  userEmail,
}) => {
  const [email, setEmail] = React.useState(userEmail || '');
  const [emailError, setEmailError] = React.useState('');
  const activePreset = getActivePreset(dateFrom, dateTo, minDate);

  React.useEffect(() => {
    if (isVisible) {
      setEmail(userEmail || '');
      setEmailError('');
    }
  }, [isVisible, userEmail]);

  const applyPreset = preset => {
    const to = moment()
      .endOf('day')
      .toDate();
    let from;

    if (preset.useMinDate && minDate) {
      from = moment(minDate).toDate();
    } else if (preset.hours) {
      from = moment()
        .subtract(preset.hours, 'hours')
        .toDate();
    } else if (preset.days) {
      from = moment()
        .subtract(preset.days, 'days')
        .startOf('day')
        .toDate();
    }

    if (from) {
      handleDateFrom(from);
    }
    handleDateTo(to);
  };

  const handleGenerate = () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setEmailError('Enter a valid email address.');
      return;
    }

    saveChanges(trimmedEmail);
  };

  return (
    <Modal
      title="Generate CSV report"
      onClose={switchModal}
      isVisible={isVisible}
      size="medium"
      zIndex={3500}
    >
      <Shell>
        <Section>
          <SectionHeader>
            <Icon name="calendar-alt" color={Colors.lightBlue} fontSize={16} />
            <SectionTitle>Date range</SectionTitle>
          </SectionHeader>

          <PresetRow>
            {QUICK_RANGES.map(preset => (
              <PresetButton
                key={preset.id}
                type="button"
                active={activePreset === preset.id}
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </PresetButton>
            ))}
          </PresetRow>

          <DateGrid>
            <div>
              <FieldLabel htmlFor="csv-date-from">From</FieldLabel>
              <DateInputWrap>
                <DatePickerComponent minDate={minDate} date={dateFrom} onChange={handleDateFrom} />
                <Icon name="calendar-alt" color={Colors.lightBlue} fontSize={16} />
              </DateInputWrap>
            </div>
            <div>
              <FieldLabel htmlFor="csv-date-to">To</FieldLabel>
              <DateInputWrap>
                <DatePickerComponent minDate={minDate} date={dateTo} onChange={handleDateTo} />
                <Icon name="calendar-alt" color={Colors.lightBlue} fontSize={16} />
              </DateInputWrap>
            </div>
          </DateGrid>
        </Section>

        <Section>
          <SectionHeader>
            <Icon name="file-export" color={Colors.lightBlue} fontSize={16} />
            <SectionTitle>Data type</SectionTitle>
          </SectionHeader>

          <ChipRow>
            {CSV_DATA_TYPES.map(type => (
              <DataTypeChip
                key={type.value}
                type="button"
                active={csvType === type.value}
                onClick={() => changeCsvType('csvType', type.value)}
              >
                {type.label}
              </DataTypeChip>
            ))}
          </ChipRow>
        </Section>

        <Section>
          <SectionHeader>
            <Icon name="envelope" color={Colors.lightBlue} fontSize={16} />
            <SectionTitle>Delivery email</SectionTitle>
          </SectionHeader>

          <FieldLabel htmlFor="csv-delivery-email">Email address</FieldLabel>
          <EmailInput
            id="csv-delivery-email"
            type="email"
            value={email}
            hasError={Boolean(emailError)}
            placeholder="name@example.com"
            onChange={event => {
              setEmail(event.target.value);
              if (emailError) {
                setEmailError('');
              }
            }}
          />
          {emailError ? (
            <EmailError>{emailError}</EmailError>
          ) : (
            <EmailHint>
              The CSV is generated in the background and sent to this email when it is ready. This
              usually takes a few minutes.
            </EmailHint>
          )}
        </Section>

        <SummaryBar>
          <Icon name="info-circle" color={Colors.lightBlue} fontSize={16} />
          <SummaryText>
            {moment(dateFrom).format('MMM D, YYYY h:mm A')} –{' '}
            {moment(dateTo).format('MMM D, YYYY h:mm A')} · {getDataTypeLabel(csvType)}
          </SummaryText>
        </SummaryBar>

        <Footer>
          <ButtonText text="Cancel" theme="secondary" onClick={switchModal} />
          <ButtonText text="Generate" theme="primary" onClick={handleGenerate} />
        </Footer>
      </Shell>
    </Modal>
  );
};

export default ModalCsvFiles;

ModalCsvFiles.propTypes = {
  changeCsvType: PropTypes.func,
  csvType: PropTypes.number,
  dateFrom: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  dateTo: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  handleDateFrom: PropTypes.func,
  handleDateTo: PropTypes.func,
  isVisible: PropTypes.bool,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  saveChanges: PropTypes.func,
  switchModal: PropTypes.func,
  userEmail: PropTypes.string,
};
